'use client';

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { fetchBlogs, subscribeToNews } from '../api';
import authorInfo from '../../assets/images/auther_info.svg';
import calendarPlugin from '../../assets/images/calander_plugin.svg';
import heartDetails from '../../assets/images/heart_details.svg';
import chatDetails from '../../assets/images/chat_details.svg';
import MetaUpdater from '../metadata/MetaUpdater';
import toast, { Toaster } from 'react-hot-toast';
import search from '../../assets/images/search_iconnes.svg';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;

// Define interfaces to match backend
interface LocalizedField {
  en: string;
  ar: string;
}

interface Comment {
  _id: string;
  user: string;
  text: LocalizedField;
  approved: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BlogPost {
  _id: string;
  serviceTitle: LocalizedField;
  serviceTopic: LocalizedField;
  description: LocalizedField;
  tag: LocalizedField;
  priority: number;
  isActive: boolean;
  image: string;
  createdAt: string;
  likes: number;
  comments: Comment[];
}

const titleToSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const Blog: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [filteredBlogs, setFilteredBlogs] = useState<BlogPost[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const postsPerPage = 6;
  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';
  const [email, setEmail] = useState('');

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await fetchBlogs();
        const activeBlogs = data.blogs
          .filter((b: BlogPost) => b.isActive)
          .sort((a: BlogPost, b: BlogPost) => a.priority - b.priority);
        setBlogs(activeBlogs);
        setFilteredBlogs(activeBlogs);
        setLoading(false);
      } catch (err) {
        setError(t('error_message'));
        setLoading(false);
      }
    };
    loadBlogs();
  }, [t, currentLang]);

  useEffect(() => {
    let filtered = blogs;
    if (selectedTitle) {
      filtered = filtered.filter(
        (b) => b.serviceTitle[currentLang] === selectedTitle
      );
    } else if (selectedTag) {
      filtered = filtered.filter((b) =>
        b.tag[currentLang]
          .split(',')
          .map((t) => t.trim())
          .includes(selectedTag)
      );
    }
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.serviceTitle[currentLang].toLowerCase().includes(lowerQuery) ||
          b.serviceTopic[currentLang].toLowerCase().includes(lowerQuery) ||
          b.tag[currentLang].toLowerCase().includes(lowerQuery)
      );
    }
    setFilteredBlogs(filtered);
    setCurrentPage(1);
  }, [selectedTitle, selectedTag, searchQuery, blogs, currentLang]);

  const tags = Array.from(
    new Set(
      blogs
        .flatMap((b) => b.tag[currentLang].split(',').map((t) => t.trim()))
        .filter((t) => t)
    )
  );

  const titles = Array.from(
    new Set(blogs.map((b) => b.serviceTitle[currentLang]))
  );

  const totalPages = Math.ceil(filteredBlogs.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredBlogs.slice(indexOfFirstPost, indexOfLastPost);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBlogClick = (title: string) => {
    navigate(`/blog/${titleToSlug(title)}`);
  };

  const handleTitleClick = (title: string) => {
    setSelectedTitle(title === selectedTitle ? null : title);
    setSelectedTag(null);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag === selectedTag ? null : tag);
    setSelectedTitle(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedTitle(null);
    setSelectedTag(null);
  };

  const handleSubscribe = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }

    try {
      await subscribeToNews(email);
      toast.success('Subscribed successfully!');
      setEmail('');
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong.');
    }
  };
  return (
    <>
      <MetaUpdater page="blogs" />
      <Toaster position="top-right" />
      {/* Hero Section */}
      <section
        className="blogg_heros"
        dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <div className="main-title">
                <h2>{t('Resources_of_Shah_LPO')}</h2>
                <p>{t('Trust_Expertise_Results')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="blogg_info">
        <div className="container">
          <div className="row d-md-none mb-3">
            <div className="col-12">
              <div className="serach-box">
                <input
                  className="form-control me-2"
                  type="search"
                  placeholder={t('search')}
                  aria-label="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
                />
                {searchQuery ? (
                  <span onClick={() => setSearchQuery('')} />
                ) : (
                  <img src={search} alt="search icon" className="serach-icon" />
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div
              className="col-md-8"
              dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
            >
              <div className="blogg-wrapes">
                {error ? (
                  <p>{error}</p>
                ) : currentPosts.length === 0 ? (
                  <p>{'No blog posts found'}</p>
                ) : (
                  currentPosts.map((post) => (
                    <div
                      key={post._id}
                      className="blogg_details"
                      onClick={() => handleBlogClick(post.serviceTitle.en)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card">
                        <img
                          src={`${IMAGE_BASE_URL}/${post.image}`}
                          className="card-img-top"
                          alt={post.serviceTitle[currentLang]}
                        />
                        <div className="card-body">
                          <h5 className="card-title">
                            {post.serviceTitle[currentLang].substring(0, 50)}
                            {post.serviceTitle[currentLang].length > 50
                              ? '...'
                              : ''}
                          </h5>
                          <h6>{post.serviceTopic[currentLang]}</h6>
                          <div className="auther">
                            <div className="auther_name">
                              <div className="ico">
                                <img
                                  src={authorInfo}
                                  alt={t('author_icon_alt')}
                                />
                              </div>
                              <div className="auther_name">
                                <span>{t('author')}</span>
                              </div>
                            </div>
                            <div className="auther_name">
                              <div className="ico">
                                <img
                                  src={calendarPlugin}
                                  alt={t('calendar_icon_alt')}
                                />
                              </div>
                              <div className="auther_name">
                                <span>
                                  {new Date(post.createdAt).toLocaleDateString(
                                    currentLang === 'en' ? 'en-GB' : 'ar-EG',
                                    {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                    }
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="auther_name">
                              <div className="ico">
                                <img
                                  src={heartDetails}
                                  alt={t('like_icon_alt')}
                                />
                              </div>
                              <div className="auther_name">
                                <span>{post.likes || 0}</span>
                              </div>
                            </div>
                            <div className="auther_name">
                              <div className="ico">
                                <img
                                  src={chatDetails}
                                  alt={t('comment_icon_alt')}
                                />
                              </div>
                              <div className="auther_name">
                                <span>{post.comments.length || 0}</span>
                              </div>
                            </div>
                          </div>
                          <p className="card-text">
                            {post.description[currentLang].substring(0, 100)}
                            {post.description[currentLang].length > 100
                              ? '...'
                              : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="col-md-4">
              <div className="right_side_sec">
                <div className="serach-box search-box">
                  <input
                    className="form-control me-2"
                    type="search"
                    placeholder={t('search')}
                    aria-label="Search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
                  />
                  {searchQuery ? (
                    <span
                      // className="clear-icon"
                      onClick={() => setSearchQuery('')}
                    ></span>
                  ) : (
                    <img
                      src={search}
                      alt="search icon"
                      className="serach-icon search-icon"
                    />
                  )}
                </div>
                <div
                  className="category"
                  dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
                >
                  <div className="main-title">
                    <h3>{t('Categories')}</h3>
                  </div>
                  <div className="white_boxes_category">
                    {titles.map((title) => (
                      <div className="dropdown_sec" key={title}>
                        <Link
                          to="#"
                          className="title-item"
                          onClick={(e) => {
                            e.preventDefault();
                            handleTitleClick(title);
                          }}
                          style={{
                            color: selectedTitle === title ? '#DB2217' : '#000',
                          }}
                        >
                          {title.length > 25
                            ? `${title.substring(0, 25)}...`
                            : title}{' '}
                          {t('services')}
                        </Link>
                      </div>
                    ))}
                  </div>

                  <div className="tag_sec">
                    <div className="main-title">
                      <h3>{t('Tags')}</h3>
                    </div>
                    <div className="tag_list">
                      {tags.map((tag) => (
                        <div
                          className="tag_wrapper"
                          key={tag}
                          onClick={() => handleTagClick(tag)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Link
                            to="#"
                            style={{
                              color: selectedTag === tag ? '#DB2217' : '#000',
                              textDecoration: 'none',
                            }}
                          >
                            {tag}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter_demo" dir={'ltr'}>
                    <div className="main-title">
                      <h5>{t('News  ')}</h5>
                      <div className="d-flex justify-content-center align-items-center g-20">
                        <div className="input_wrappes">
                          <input
                            type="email"
                            className="form-control"
                            id="exampleFormControlInput1"
                            placeholder={t('place_email')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                        <div className="subscribe">
                          <button
                            type="button"
                            className="btn btn-subscribe"
                            style={{
                              outline: 'none',
                              boxShadow: 'none',
                              border: 'none',
                              background: '#082341',
                              color: '#fff',
                              padding: '8px 16px',
                              cursor: 'pointer',
                            }}
                            onClick={handleSubscribe}
                          >
                            {t('Subscribe')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="row">
          <div className="col-md-12 d-flex justify-content-center">
            <nav aria-label="Page navigation example">
              <ul className="pagination" style={{ boxShadow: 'none' }}>
                <li
                  className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                >
                  <Link
                    className="page-link"
                    to="#"
                    style={{ boxShadow: 'none' }}
                    aria-label={t('previous')}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePrevPage();
                    }}
                  >
                    <span aria-hidden="true">
                      <i className="bi bi-chevron-left"></i>
                    </span>
                  </Link>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li
                    className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                    key={index}
                  >
                    <Link
                      className="page-link"
                      to="#"
                      style={{
                        boxShadow: 'none',
                        color: currentPage === index + 1 ? '#fff' : '#000',
                        backgroundColor:
                          currentPage === index + 1 ? '#082341' : 'transparent',
                        borderColor:
                          currentPage === index + 1 ? '#DB2217' : '#082341',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(index + 1);
                      }}
                    >
                      {index + 1}
                    </Link>
                  </li>
                ))}
                <li
                  className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                >
                  <Link
                    className="page-link"
                    to="#"
                    style={{ boxShadow: 'none' }}
                    aria-label={t('next')}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNextPage();
                    }}
                  >
                    <span aria-hidden="true">
                      <i className="bi bi-chevron-right"></i>
                    </span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      {currentLang === 'ar' && (
        <style>{`
    .category .main-title h3::after{
      left: 77%;
    }
    .newsletter_demo{
      left: 35px;
    }
      @media (max-width: 768px) {
      .serach-box .form-control{
      display:non
      }
  .serach-box {
    margin-bottom: 15px;
    position: relative;
  }
  .serach-box input {
    padding-right: 40px;
  }
  .serach-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    pointer-events: none;
  }
}

  `}</style>
      )}
    </>
  );
};

export default Blog;
